import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { sanitiseString } from '@quickstock/shared';

import { mutate } from '../lib/mutate';
import { Field, inputClass, Modal } from './Modal';
import { StoredNameHint } from './StoredNameHint';

export interface EditableCategory {
  categoryId: number;
  name: string;
  products: number;
  manualKeys: number;
  ageRestricted: boolean;
  discountApplicable: boolean;
  profitable: boolean;
}

/**
 * Adds or edits a department.
 *
 * The two flags interact: `category_check` requires `isprofitable OR NOT
 * isdiscountapplicable`, so a department marked unprofitable cannot allow
 * discounts. The form enforces that as you type rather than letting the database
 * reject the save — turning off "Profitable" clears and disables "discounts",
 * which shows the rule instead of describing it.
 *
 * The name is normalised the way the till normalises it, and the stored form is
 * shown as it is typed — see `StoredNameHint`. The server normalises again;
 * this is so the person sees what they are about to create, not the only guard.
 */
export function CategoryDialog({
  storeId,
  category,
  onClose,
}: {
  storeId: string;
  category: EditableCategory | null;
  onClose: () => void;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState(category?.name ?? '');
  const [profitable, setProfitable] = useState(category?.profitable ?? true);
  const [discountApplicable, setDiscountApplicable] = useState(
    category?.discountApplicable ?? true,
  );

  const isEditing = category !== null;

  const save = useMutation({
    mutationFn: (body: {
      name: string;
      ageRestricted: boolean;
      discountApplicable: boolean;
      profitable: boolean;
    }) =>
      isEditing
        ? mutate.patch(`/stores/${storeId}/categories/${category.categoryId}`, body)
        : mutate.post(`/stores/${storeId}/categories`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['reference', storeId] });
      void queryClient.invalidateQueries({ queryKey: ['products', storeId] });
      setError(null);
      onClose();
    },
    onError: (caught: unknown) =>
      setError(caught instanceof Error ? caught.message : 'Could not save.'),
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(null);

    const form = new FormData(event.currentTarget);
    save.mutate({
      name: sanitiseString(name),
      ageRestricted: form.get('ageRestricted') === 'on',
      discountApplicable,
      profitable,
    });
  }

  return (
    <Modal open title={isEditing ? 'Edit department' : 'Add a department'} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <Field label="Name" htmlFor="name">
          <input
            id="name"
            name="name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            className={inputClass}
            maxLength={50}
            required
            autoFocus
          />
        </Field>

        <StoredNameHint name={name} />

        <fieldset className="mb-3">
          <legend className="qs-eyebrow mb-1.5">Behaviour</legend>

          {/* `items-start` with the box nudged down by a pixel, so it centres on
              the first line of the label rather than on the whole block — two of
              these carry a second line of explanation underneath. `shrink-0`
              because a flex item with a fixed size will still be squeezed by a
              long label otherwise, which turned the tick box into a slot. */}
          <label className="mb-2 flex items-start gap-2 py-1 text-xs">
            <input
              type="checkbox"
              name="ageRestricted"
              defaultChecked={category?.ageRestricted ?? false}
              className="mt-px shrink-0"
            />
            <span>
              Age restricted
              <span className="block text-2xs text-ink-faint">
                The till prompts for age verification.
              </span>
            </span>
          </label>

          <label className="mb-2 flex items-start gap-2 py-1 text-xs">
            <input
              type="checkbox"
              checked={profitable}
              onChange={(event) => {
                setProfitable(event.target.checked);
                // Keeping discounts on while turning profitability off would
                // violate category_check on save.
                if (!event.target.checked) setDiscountApplicable(false);
              }}
              className="mt-px shrink-0"
            />
            <span>Profitable</span>
          </label>

          <label className="flex items-start gap-2 py-1 text-xs">
            <input
              type="checkbox"
              checked={discountApplicable}
              disabled={!profitable}
              onChange={(event) => setDiscountApplicable(event.target.checked)}
              className="mt-px shrink-0"
            />
            <span className={profitable ? undefined : 'text-ink-faint'}>
              Discounts allowed
              {!profitable && (
                <span className="block text-2xs">
                  Unavailable while the department is not profitable.
                </span>
              )}
            </span>
          </label>
        </fieldset>

        {/* Manual keys are named alongside products: the till applies these
            flags to them too, so a department holding only a manual key still
            has something that changes. */}
        {isEditing && category.products + category.manualKeys > 0 && (
          <p className="mb-3 rounded border border-rule bg-sunken px-2.5 py-2 text-2xs text-ink-muted">
            {describeContents(category)} in this department. Changing these settings affects
            all of them.
          </p>
        )}

        {error && (
          <p role="alert" className="mb-3 text-xs text-down">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-rule px-3 py-1.5 text-xs text-ink-muted hover:text-ink"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={save.isPending}
            className="rounded bg-accent px-3 py-1.5 text-xs font-medium text-ink-inverse hover:bg-accent-hover disabled:opacity-60"
          >
            {save.isPending ? 'Saving…' : isEditing ? 'Save' : 'Add'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function describeContents({ products, manualKeys }: EditableCategory): string {
  const parts: string[] = [];
  if (products > 0) {
    parts.push(`${products.toLocaleString('en-GB')} ${products === 1 ? 'product' : 'products'}`);
  }
  if (manualKeys > 0) {
    parts.push(`${manualKeys} manual ${manualKeys === 1 ? 'key' : 'keys'}`);
  }
  const verb = products + manualKeys === 1 ? 'is' : 'are';
  return `${parts.join(' and ')} ${verb}`;
}
